import { useCallback, useEffect, useState } from "react"

import { fetchAvailableProviders } from "src/features/integrations/api/providersApi"

const STORAGE_PREFIX = "clozdl.providerChoice."

function readRemembered(channel) {
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + channel) || null
  } catch {
    return null
  }
}

function writeRemembered(channel, provider) {
  try {
    if (provider) window.localStorage.setItem(STORAGE_PREFIX + channel, provider)
  } catch {
    /* localStorage unavailable (private mode) — non-fatal, just skip persistence */
  }
}

// Loads the enabled+configured providers for a channel and tracks the user's
// preferred one. `providers` is the list to offer in a picker; `hasChoice` is
// true only when there are 2+ (the signal to show a chooser at all). The
// preferred provider is the last remembered choice, else the org's routing
// default — callers pass it straight through to placeCall / sendTemplateMessage.
export function useChannelProviders(channel) {
  const [providers, setProviders] = useState([])
  const [remembered, setRemembered] = useState(() => readRemembered(channel))
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    setLoading(true)
    fetchAvailableProviders()
      .then((data) => {
        if (!active) return
        setProviders(data?.channels?.[channel] || [])
      })
      .catch(() => {
        if (active) setProviders([])
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [channel])

  const remember = useCallback(
    (provider) => {
      setRemembered(provider)
      writeRemembered(channel, provider)
    },
    [channel]
  )

  const defaultKey = providers.find((p) => p.default)?.key || providers[0]?.key || null
  // Only honour a remembered choice that is still an available option.
  const rememberedValid = providers.some((p) => p.key === remembered) ? remembered : null
  const preferred = rememberedValid || defaultKey

  return {
    loading,
    providers,
    hasChoice: providers.length > 1,
    // True only once the fetch has settled with zero configured providers — the
    // signal to fall back to dialing via the user's mobile app.
    none: !loading && providers.length === 0,
    preferred,
    remember,
  }
}
